import type {
  CommunityCanvas,
  CommunityLogo,
  CommunityLogoStyle,
  CommunityShape,
  CommunityShapeKind,
  CommunityTextBox,
  CommunityTextFill,
  CommunityTextRole,
  CommunityZones,
  CommunityZonesV1,
  CommunityZonesV2,
  PortraitZone,
  ZoneBox,
} from '../../types/api';

/**
 * The zone map's rules, in one place: the limits every layer is held to, the
 * map a new template starts from, the upgrade from a v1 map, and the clamping
 * that keeps every box on the canvas.
 *
 * The backend enforces the same numbers (`schemas/community.py`,
 * COMMUNITY_TEMPLATES_V2_PLAN.md §1), so this module is a mirror of its
 * validators rather than a second opinion — change one side, change the other.
 * Nothing here touches React, storage or the API; it is arithmetic over the
 * fractions the map is written in.
 */

/** The map version the studio writes. v1 maps are upgraded on load. */
export const ZONES_VERSION = 2;

/* ------------------------------------------------------------------ */
/* Limits                                                              */
/* ------------------------------------------------------------------ */

/** v1's caption zone minimum, kept so a v1 map still reads as it was validated. */
export const V1_TEXT_MIN_W = 0.25;
export const V1_TEXT_MIN_H = 0.08;

/** A v2 text box: looser than v1's caption because a label is small. */
export const TEXT_MIN_W = 0.1;
export const TEXT_MIN_H = 0.04;

/** A logo box edge, when the logo is enabled. */
export const LOGO_MIN_EDGE = 0.06;

/** A shape box edge. A line is a rule along the longer edge. */
export const SHAPE_MIN_EDGE = 0.01;

/** A value box. It holds a short number, so it may be small. */
export const VALUE_MIN_W = 0.05;
export const VALUE_MIN_H = 0.05;

/** The portrait circle's diameter bounds. */
export const PORTRAIT_MIN_D = 0.1;
export const PORTRAIT_MAX_D = 0.6;

export const TEXTS_MIN = 1;
export const TEXTS_MAX = 8;
export const SHAPES_MAX = 12;

export const LABEL_MAX_LENGTH = 40;
export const MAX_CHARS_MIN = 1;
export const MAX_CHARS_MAX = 300;
export const SIZE_MAX_MIN = 14;
export const SIZE_MAX_MAX = 220;
export const SIZE_MIN_MIN = 12;
export const MAX_LINES_MIN = 1;
export const MAX_LINES_MAX = 12;
export const TRACKING_MIN = 0;
export const TRACKING_MAX = 20;
export const LINE_SPACING_MIN = 0.9;
export const LINE_SPACING_MAX = 2;
export const OPACITY_MIN = 0.05;
export const OPACITY_MAX = 1;
export const RADIUS_MIN = 0;
export const RADIUS_MAX = 0.5;
export const THICKNESS_MIN = 0.001;
export const THICKNESS_MAX = 0.05;
export const ANGLE_STEP = 45;

/** The most characters an author's AI instructions for one box may run to. */
export const AI_HINT_MAX = 200;

/** The smallest character limit a box the AI fills may have. */
export const AI_MIN_CHARS = 3;

/** Who may fill a text box, in the order the studio offers them. */
export const TEXT_FILLS: readonly CommunityTextFill[] = ['user', 'ai'];

/** Whether the AI writes this box when the member leaves it blank. */
export function isAiBox(text: CommunityTextBox): boolean {
  return text.fill === 'ai';
}

/** The canvas the renderer draws on, in px. Sizes in the map are px on it. */
export const CANVAS_PX = 1080;

/** The id rule for text boxes and shapes, unique across the map. */
export const LAYER_ID_PATTERN = /^[a-z][a-z0-9_]{0,23}$/;

/** A font catalogue slug, as the backend's `CatalogFont.slug` is written. */
export const FONT_SLUG_PATTERN = /^[a-z0-9-]{1,40}$/;

/**
 * A text box's font as the map may hold it: a well-formed catalogue slug, or
 * null for the theme font. Anything else — a number, an over-long string, a
 * name with capitals or spaces — reads as the theme font, which is what the
 * renderer does with a slug it cannot use.
 */
export function readFontSlug(value: unknown): string | null {
  return typeof value === 'string' && FONT_SLUG_PATTERN.test(value) ? value : null;
}

/** What {@link zoneProblems} needs of the font catalogue: whether a slug is in it. */
export interface KnownFonts {
  has(slug: string): boolean;
}

/** A fixed colour, or one of the three brand roles. */
const HEX_PATTERN = /^#[0-9A-Fa-f]{6}$/;
export const BRAND_ROLES = ['brand:primary', 'brand:secondary', 'brand:accent'] as const;
export type BrandRoleRef = (typeof BRAND_ROLES)[number];

/** What each brand role is called on screen. */
export const BRAND_ROLE_NAMES: Record<BrandRoleRef, string> = {
  'brand:primary': 'Primary',
  'brand:secondary': 'Secondary',
  'brand:accent': 'Accent',
};

/** A colour ref said in words, for the layers list and a screen reader. */
export function colorName(ref: string): string {
  if (isBrandRole(ref)) return `Brand ${BRAND_ROLE_NAMES[ref].toLowerCase()}`;
  if (ref === 'auto') return 'Auto';
  if (ref === 'light') return 'Light';
  if (ref === 'dark') return 'Dark';
  return ref.toUpperCase();
}

/** The text colour keywords beside a colour ref. */
export const TEXT_COLOR_KEYWORDS = ['auto', 'light', 'dark'] as const;

/** What `light` and `dark` mean, and the neutral palette fallbacks. */
export const LIGHT_INK = '#F7F5F0';
export const DARK_INK = '#1C1B18';
export const FALLBACK_PRIMARY = '#1C1B18';
export const FALLBACK_SECONDARY = '#4A4640';

export function isHexColor(value: string): boolean {
  return HEX_PATTERN.test(value);
}

export function isBrandRole(value: string): value is BrandRoleRef {
  return (BRAND_ROLES as readonly string[]).includes(value);
}

/** A backdrop or shape colour: `#RRGGBB` or a brand role. */
export function isColorRef(value: string): boolean {
  return isHexColor(value) || isBrandRole(value);
}

/** A text colour: a colour ref, or `auto` / `light` / `dark`. */
export function isTextColor(value: string): boolean {
  return isColorRef(value) || (TEXT_COLOR_KEYWORDS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ */
/* Brand palette                                                       */
/* ------------------------------------------------------------------ */

/** The three roles a design can name, resolved against one business's palette. */
export interface BrandPalette {
  primary: string;
  secondary: string;
  accent: string;
}

/** Mirrors the backend's `PRIMARY_COLOR_KEYS` / `SECONDARY_COLOR_KEYS`. */
const PRIMARY_COLOR_KEYS = ['primary', 'brand', 'main', 'background'];
const SECONDARY_COLOR_KEYS = ['secondary', 'accent', 'highlight'];

function paletteColor(colors: Record<string, string>, keys: string[], fallback: string): string {
  for (const key of keys) {
    const value = colors[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return fallback;
}

/** The neutral palette, for a business with none on file or a signed-out view. */
export const NEUTRAL_PALETTE: BrandPalette = {
  primary: FALLBACK_PRIMARY,
  secondary: FALLBACK_SECONDARY,
  accent: FALLBACK_SECONDARY,
};

/**
 * `brand_identity.colors` read by the backend's own rule: first matching key,
 * else the neutral default. Accent is the secondary — the renderer's theme
 * accent is the secondary unless a theme imposes one, and a community design
 * is drawn without a theme.
 */
export function brandPalette(colors: Record<string, string> | null | undefined): BrandPalette {
  if (!colors) return NEUTRAL_PALETTE;
  const primary = paletteColor(colors, PRIMARY_COLOR_KEYS, FALLBACK_PRIMARY);
  const secondary = paletteColor(colors, SECONDARY_COLOR_KEYS, FALLBACK_SECONDARY);
  return { primary, secondary, accent: secondary };
}

/** A colour ref as CSS. An unreadable one falls back to the primary. */
export function resolveColor(ref: string, palette: BrandPalette): string {
  switch (ref) {
    case 'brand:primary':
      return palette.primary;
    case 'brand:secondary':
      return palette.secondary;
    case 'brand:accent':
      return palette.accent;
    default:
      return isHexColor(ref) ? ref : palette.primary;
  }
}

/** Relative luminance of a `#RRGGBB`, 0..1. Anything else reads as mid-grey. */
export function luminance(hex: string): number {
  if (!isHexColor(hex)) return 0.5;
  const channel = (offset: number) => {
    const value = parseInt(hex.slice(offset, offset + 2), 16) / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/* ------------------------------------------------------------------ */
/* Fractions                                                           */
/* ------------------------------------------------------------------ */

/**
 * Fractions cross the wire in ten-thousandths, and every clamp works in those
 * units rather than in floats: `x + w <= 1` then holds exactly, which is what
 * the server checks.
 */
const UNITS = 10000;

function toUnits(value: number): number {
  return Number.isFinite(value) ? Math.round(value * UNITS) : 0;
}

/** Fractions cross the wire, so keep them short and exactly comparable. */
export function roundFraction(value: number): number {
  return toUnits(value) / UNITS;
}

function clampNumber(value: number, low: number, high: number): number {
  if (!Number.isFinite(value)) return low;
  return Math.min(Math.max(value, low), Math.max(low, high));
}

function clampInteger(value: number, low: number, high: number): number {
  return clampNumber(Math.round(value), low, high);
}

/** Minimum size of one box, by which layer it is. */
export interface ZoneMinimum {
  w: number;
  h: number;
}

export const TEXT_MINIMUM: ZoneMinimum = { w: TEXT_MIN_W, h: TEXT_MIN_H };
export const LOGO_MINIMUM: ZoneMinimum = { w: LOGO_MIN_EDGE, h: LOGO_MIN_EDGE };
export const SHAPE_MINIMUM: ZoneMinimum = { w: SHAPE_MIN_EDGE, h: SHAPE_MIN_EDGE };
export const VALUE_MINIMUM: ZoneMinimum = { w: VALUE_MIN_W, h: VALUE_MIN_H };

/**
 * Puts a box inside the canvas at its minimum size or larger: the size is
 * settled first, then the corner is pulled back far enough that the box fits.
 */
export function clampBox(box: ZoneBox, minimum: ZoneMinimum): ZoneBox {
  const w = clampNumber(toUnits(box.w), toUnits(minimum.w), UNITS);
  const h = clampNumber(toUnits(box.h), toUnits(minimum.h), UNITS);
  const x = clampNumber(toUnits(box.x), 0, UNITS - w);
  const y = clampNumber(toUnits(box.y), 0, UNITS - h);
  return { x: x / UNITS, y: y / UNITS, w: w / UNITS, h: h / UNITS };
}

/** Which corner of a box a resize grip is on. */
export type ResizeCorner = 'nw' | 'ne' | 'sw' | 'se';

/**
 * A box resized from one corner toward a point, the opposite corner held
 * still: the moving edges stop at the canvas and at the minimum size.
 */
export function resizeBox(
  box: ZoneBox,
  corner: ResizeCorner,
  point: { fx: number; fy: number },
  minimum: ZoneMinimum,
): ZoneBox {
  const minW = toUnits(minimum.w);
  const minH = toUnits(minimum.h);
  let left = toUnits(box.x);
  let top = toUnits(box.y);
  let right = left + toUnits(box.w);
  let bottom = top + toUnits(box.h);
  const px = toUnits(point.fx);
  const py = toUnits(point.fy);

  if (corner === 'nw' || corner === 'sw') left = clampNumber(px, 0, right - minW);
  else right = clampNumber(px, left + minW, UNITS);
  if (corner === 'nw' || corner === 'ne') top = clampNumber(py, 0, bottom - minH);
  else bottom = clampNumber(py, top + minH, UNITS);

  return {
    x: left / UNITS,
    y: top / UNITS,
    w: (right - left) / UNITS,
    h: (bottom - top) / UNITS,
  };
}

/**
 * Puts the portrait circle wholly on the canvas: the diameter is held between
 * its bounds first, then the centre is pulled in by the radius.
 */
export function clampPortrait(portrait: PortraitZone): PortraitZone {
  const d = clampNumber(toUnits(portrait.d), toUnits(PORTRAIT_MIN_D), toUnits(PORTRAIT_MAX_D));
  const radius = Math.round(d / 2);
  const cx = clampNumber(toUnits(portrait.cx), radius, UNITS - radius);
  const cy = clampNumber(toUnits(portrait.cy), radius, UNITS - radius);
  return { cx: cx / UNITS, cy: cy / UNITS, d: d / UNITS };
}

/** The largest diameter that still fits at this centre, in fractions. */
export function maxDiameterAt(cx: number, cy: number): number {
  const room = Math.min(cx, 1 - cx, cy, 1 - cy) * 2;
  return Math.min(PORTRAIT_MAX_D, Math.max(PORTRAIT_MIN_D, roundFraction(room)));
}

/** The portrait's bounding square, as a box — how the canvas places it. */
export function portraitBox(portrait: PortraitZone): ZoneBox {
  return {
    x: portrait.cx - portrait.d / 2,
    y: portrait.cy - portrait.d / 2,
    w: portrait.d,
    h: portrait.d,
  };
}

/* ------------------------------------------------------------------ */
/* Defaults and presets                                                */
/* ------------------------------------------------------------------ */

/** Where a portrait circle appears when the author switches it on. */
export const DEFAULT_PORTRAIT: PortraitZone = { cx: 0.5, cy: 0.33, d: 0.3 };

/** Where a value box appears when the author switches it on. */
export const DEFAULT_VALUE: ZoneBox = { x: 0.68, y: 0.08, w: 0.24, h: 0.14 };

/** Where the logo sits on a new design. */
export const DEFAULT_LOGO_BOX: ZoneBox = { x: 0.08, y: 0.06, w: 0.16, h: 0.16 };

/** What each role is called, and what a new box of it starts as. */
interface RolePreset {
  label: string;
  sample: string;
  maxChars: number;
  box: ZoneBox;
  weight: CommunityTextBox['weight'];
  sizeMax: number;
  sizeMin: number;
  maxLines: number;
  uppercase: boolean;
  tracking: number;
  lineSpacing: number;
}

export const TEXT_ROLES: Record<CommunityTextRole, RolePreset> = {
  headline: {
    label: 'Headline',
    sample: 'Now open on Main Street',
    maxChars: 60,
    box: { x: 0.08, y: 0.6, w: 0.84, h: 0.2 },
    weight: 'bold',
    sizeMax: 76,
    sizeMin: 40,
    maxLines: 3,
    uppercase: false,
    tracking: 0,
    lineSpacing: 1.15,
  },
  subheading: {
    label: 'Subheading',
    sample: 'Fresh bread every morning',
    maxChars: 80,
    box: { x: 0.08, y: 0.8, w: 0.84, h: 0.08 },
    weight: 'regular',
    sizeMax: 40,
    sizeMin: 26,
    maxLines: 2,
    uppercase: false,
    tracking: 0,
    lineSpacing: 1.2,
  },
  body: {
    label: 'Body',
    sample: 'Come by this week and taste the new range.',
    maxChars: 180,
    box: { x: 0.08, y: 0.4, w: 0.84, h: 0.16 },
    weight: 'regular',
    sizeMax: 34,
    sizeMin: 22,
    maxLines: 5,
    uppercase: false,
    tracking: 0,
    lineSpacing: 1.3,
  },
  eyebrow: {
    label: 'Eyebrow',
    sample: 'New this week',
    maxChars: 30,
    box: { x: 0.08, y: 0.52, w: 0.5, h: 0.05 },
    weight: 'bold',
    sizeMax: 26,
    sizeMin: 18,
    maxLines: 1,
    uppercase: true,
    tracking: 3,
    lineSpacing: 1.1,
  },
  cta: {
    label: 'Call to action',
    sample: 'Book a table',
    maxChars: 30,
    box: { x: 0.08, y: 0.88, w: 0.4, h: 0.06 },
    weight: 'bold',
    sizeMax: 32,
    sizeMin: 22,
    maxLines: 1,
    uppercase: false,
    tracking: 1,
    lineSpacing: 1.1,
  },
  label: {
    label: 'Label',
    sample: 'Limited time',
    maxChars: 30,
    box: { x: 0.6, y: 0.08, w: 0.32, h: 0.05 },
    weight: 'regular',
    sizeMax: 24,
    sizeMin: 16,
    maxLines: 1,
    uppercase: false,
    tracking: 0,
    lineSpacing: 1.1,
  },
};

/** The roles, in the order a menu offers them. */
export const TEXT_ROLE_ORDER: readonly CommunityTextRole[] = [
  'headline',
  'subheading',
  'body',
  'eyebrow',
  'cta',
  'label',
];

export const SHAPE_KIND_LABELS: Record<CommunityShapeKind, string> = {
  rect: 'Rectangle',
  ellipse: 'Ellipse',
  line: 'Line',
};

export const LOGO_STYLE_LABELS: Record<CommunityLogoStyle, { label: string; blurb: string }> = {
  auto: { label: 'Auto', blurb: 'Bare when it reads, then a knockout, then a chip behind it.' },
  bare: { label: 'Bare', blurb: 'The mark as uploaded, nothing behind it.' },
  chip: { label: 'Chip', blurb: 'Always on a small rounded panel.' },
};

/** Every id already in the map, texts and shapes together. */
function takenIds(zones: CommunityZonesV2): Set<string> {
  return new Set([...zones.texts.map((text) => text.id), ...zones.shapes.map((shape) => shape.id)]);
}

/** The first free id on a base: `body`, `body_2`, `body_3`… within 24 characters. */
export function nextLayerId(zones: CommunityZonesV2, base: string): string {
  const taken = takenIds(zones);
  const root = base.slice(0, 20);
  if (!taken.has(root)) return root;
  for (let index = 2; index < 1000; index += 1) {
    const candidate = `${root}_${index}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${root}_${Date.now() % 1000}`;
}

/** How far a new layer steps away from one it would land on, each way per try. */
const NEW_LAYER_STEP = { x: 0.02, y: 0.04 } as const;
const NEW_LAYER_TRIES = 12;

/** The area two boxes share; zero when they only touch. */
function overlapArea(a: ZoneBox, b: ZoneBox): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 1e-9 && h > 1e-9 ? w * h : 0;
}

/**
 * A new layer's box, stepped clear of the layers of its kind already on the
 * design. The preset stands when it is free; otherwise it steps a few percent
 * at a time down and right, or up and right, whichever clears it first
 * (clamped to the canvas), and takes the nearest place that sits on nothing —
 * or, on a full design, the place that covers the least.
 */
export function clearOfOthers(
  preset: ZoneBox,
  others: readonly ZoneBox[],
  minimum: ZoneMinimum,
): ZoneBox {
  const covered = (box: ZoneBox) =>
    others.reduce((total, other) => total + overlapArea(box, other), 0);
  let best = preset;
  let bestCovered = covered(preset);
  if (bestCovered === 0) return preset;
  for (let step = 1; step <= NEW_LAYER_TRIES; step += 1) {
    for (const direction of [1, -1]) {
      const candidate = clampBox(
        {
          ...preset,
          x: preset.x + step * NEW_LAYER_STEP.x,
          y: preset.y + direction * step * NEW_LAYER_STEP.y,
        },
        minimum,
      );
      const area = covered(candidate);
      if (area === 0) return candidate;
      if (area < bestCovered) {
        best = candidate;
        bestCovered = area;
      }
    }
  }
  return best;
}

/**
 * A new text box of a role, at its preset place — stepped clear of the text
 * boxes already there — with the next free id.
 */
export function newTextBox(zones: CommunityZonesV2, role: CommunityTextRole): CommunityTextBox {
  const preset = TEXT_ROLES[role];
  return {
    id: nextLayerId(zones, role),
    role,
    label: preset.label,
    default: preset.sample,
    max_chars: preset.maxChars,
    box: clearOfOthers(
      preset.box,
      zones.texts.map((text) => text.box),
      TEXT_MINIMUM,
    ),
    align: 'left',
    valign: role === 'headline' || role === 'body' ? 'bottom' : 'middle',
    weight: preset.weight,
    size_max: preset.sizeMax,
    size_min: preset.sizeMin,
    max_lines: preset.maxLines,
    uppercase: preset.uppercase,
    tracking: preset.tracking,
    line_spacing: preset.lineSpacing,
    color: 'auto',
    font: null,
    fill: 'user',
    ai_hint: '',
  };
}

/** A new shape of a kind, stepped clear of the shapes of that kind already there, with the next free `sN` id. */
export function newShape(zones: CommunityZonesV2, kind: CommunityShapeKind): CommunityShape {
  const taken = takenIds(zones);
  let index = zones.shapes.length + 1;
  while (taken.has(`s${index}`)) index += 1;
  const box: ZoneBox =
    kind === 'line'
      ? { x: 0.08, y: 0.56, w: 0.3, h: 0.02 }
      : kind === 'ellipse'
        ? { x: 0.62, y: 0.1, w: 0.3, h: 0.3 }
        : { x: 0, y: 0.55, w: 1, h: 0.45 };
  return {
    id: `s${index}`,
    kind,
    box: clearOfOthers(
      box,
      // Only the same kind: a line laid across a panel is a design, two panels in one place are not.
      zones.shapes.filter((shape) => shape.kind === kind).map((shape) => shape.box),
      SHAPE_MINIMUM,
    ),
    fill: kind === 'rect' ? 'brand:secondary' : 'brand:accent',
    opacity: kind === 'rect' ? 0.9 : 1,
    radius: 0,
    thickness: 0.006,
  };
}

/** The map a brand-new template starts on: a brand-colour backdrop, a headline, the logo. */
export function defaultZones(): CommunityZonesV2 {
  const empty: CommunityZonesV2 = {
    v: 2,
    canvas: { kind: 'color', color: 'brand:primary' },
    shapes: [],
    texts: [],
    logo: { enabled: true, box: DEFAULT_LOGO_BOX, style: 'auto' },
    portrait: null,
    value: null,
  };
  return { ...empty, texts: [{ ...newTextBox(empty, 'headline'), color: 'light' }] };
}

/**
 * A v1 map as a v2 one, so the studio edits one shape of map: the caption
 * zone becomes one `headline` box sized to it, the logo stays enabled in its
 * box, and portrait and value carry over. Idempotent on a v2 map.
 */
export function upgradeZones(zones: CommunityZones): CommunityZonesV2 {
  if (zones.v === 2) return zones;
  const headline = TEXT_ROLES.headline;
  return {
    v: 2,
    canvas: { kind: 'image' },
    shapes: [],
    texts: [
      {
        id: 'headline',
        role: 'headline',
        label: headline.label,
        default: '',
        max_chars: 90,
        box: zones.text,
        align: zones.align,
        // The v1 renderer centres the caption block inside its zone.
        valign: 'middle',
        weight: 'bold',
        size_max: headline.sizeMax,
        size_min: headline.sizeMin,
        max_lines: 4,
        uppercase: false,
        tracking: 0,
        line_spacing: headline.lineSpacing,
        color: zones.ink,
        // v1 had no font choice: the caption was always the theme's face.
        font: null,
        // Nor AI fill: the caption was always the member's.
        fill: 'user',
        ai_hint: '',
      },
    ],
    logo: { enabled: true, box: zones.logo, style: 'auto' },
    portrait: zones.portrait,
    value: zones.value,
  };
}

/* ------------------------------------------------------------------ */
/* Clamping                                                            */
/* ------------------------------------------------------------------ */

function clampAngle(angle: number): number {
  const stepped = Math.round((Number.isFinite(angle) ? angle : 0) / ANGLE_STEP) * ANGLE_STEP;
  return ((stepped % 360) + 360) % 360;
}

function clampCanvas(canvas: CommunityCanvas): CommunityCanvas {
  switch (canvas.kind) {
    case 'image':
      return { kind: 'image' };
    case 'color':
      return { kind: 'color', color: canvas.color };
    case 'gradient':
      return {
        kind: 'gradient',
        from: canvas.from,
        to: canvas.to,
        angle: clampAngle(canvas.angle),
      };
  }
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function clampTextBox(text: CommunityTextBox): CommunityTextBox {
  const sizeMax = clampInteger(text.size_max, SIZE_MAX_MIN, SIZE_MAX_MAX);
  return {
    ...text,
    label: text.label.slice(0, LABEL_MAX_LENGTH),
    max_chars: clampInteger(text.max_chars, MAX_CHARS_MIN, MAX_CHARS_MAX),
    box: clampBox(text.box, TEXT_MINIMUM),
    size_max: sizeMax,
    size_min: clampInteger(text.size_min, SIZE_MIN_MIN, sizeMax),
    max_lines: clampInteger(text.max_lines, MAX_LINES_MIN, MAX_LINES_MAX),
    tracking: clampInteger(text.tracking, TRACKING_MIN, TRACKING_MAX),
    line_spacing: round2(clampNumber(text.line_spacing, LINE_SPACING_MIN, LINE_SPACING_MAX)),
    font: readFontSlug(text.font),
    fill: text.fill === 'ai' ? 'ai' : 'user',
    ai_hint: (text.ai_hint ?? '').slice(0, AI_HINT_MAX),
  };
}

export function clampShape(shape: CommunityShape): CommunityShape {
  return {
    ...shape,
    box: clampBox(shape.box, SHAPE_MINIMUM),
    opacity: round2(clampNumber(shape.opacity, OPACITY_MIN, OPACITY_MAX)),
    radius: round2(clampNumber(shape.radius, RADIUS_MIN, RADIUS_MAX)),
    thickness: roundFraction(clampNumber(shape.thickness, THICKNESS_MIN, THICKNESS_MAX)),
  };
}

function clampLogo(logo: CommunityLogo): CommunityLogo {
  return {
    enabled: logo.enabled,
    box: clampBox(logo.box, LOGO_MINIMUM),
    style: logo.style,
  };
}

/**
 * Every numeric field of a v2 map held inside its bounds. What the studio
 * loads and saves. It does not repair what only the author can decide — a
 * duplicate id, a second headline, an over-long default — those are what
 * {@link zoneProblems} says out loud.
 */
export function clampZones(zones: CommunityZonesV2): CommunityZonesV2 {
  return {
    v: 2,
    canvas: clampCanvas(zones.canvas),
    shapes: zones.shapes.map(clampShape),
    texts: zones.texts.map(clampTextBox),
    logo: clampLogo(zones.logo),
    portrait: zones.portrait ? clampPortrait(zones.portrait) : null,
    value: zones.value ? clampBox(zones.value, VALUE_MINIMUM) : null,
  };
}

/* ------------------------------------------------------------------ */
/* Problems                                                            */
/* ------------------------------------------------------------------ */

function percent(fraction: number): number {
  return Math.round(fraction * 100);
}

function boxProblem(box: ZoneBox, name: string, minimum: ZoneMinimum): string | null {
  if (box.w < minimum.w || box.h < minimum.h) {
    return `${name} is smaller than ${percent(minimum.w)}% by ${percent(minimum.h)}% of the design.`;
  }
  if (box.x < 0 || box.y < 0 || box.x + box.w > 1.00005 || box.y + box.h > 1.00005) {
    return `${name} hangs off the design.`;
  }
  return null;
}

function colorProblem(ref: string, name: string, allowText: boolean): string | null {
  const ok = allowText ? isTextColor(ref) : isColorRef(ref);
  return ok ? null : `${name} has a colour that is not a brand colour or a #RRGGBB hex.`;
}

function textName(text: CommunityTextBox): string {
  const label = text.label.trim();
  return `The “${label || text.id}” text box`;
}

function shapeName(shape: CommunityShape): string {
  return `Shape ${shape.id} (${SHAPE_KIND_LABELS[shape.kind].toLowerCase()})`;
}

/**
 * Everything wrong with a v2 map, in the author's words. An empty list is what
 * lets the studio save — the same rules the backend applies, said early enough
 * to fix rather than as a rejected request.
 *
 * `knownFonts` is the font catalogue once it has loaded. Until then — or when
 * the server has no catalogue to give — a well-formed slug is let through and
 * the server stays the authority on whether it is offered.
 */
export function zoneProblems(
  zones: CommunityZonesV2,
  knownFonts: KnownFonts | null = null,
): string[] {
  const problems: string[] = [];
  const push = (problem: string | null) => {
    if (problem !== null) problems.push(problem);
  };

  if (zones.texts.length < TEXTS_MIN) {
    problems.push('A design needs at least one text box.');
  }
  if (zones.texts.length > TEXTS_MAX) {
    problems.push(`A design holds at most ${TEXTS_MAX} text boxes. Remove one.`);
  }
  if (zones.shapes.length > SHAPES_MAX) {
    problems.push(`A design holds at most ${SHAPES_MAX} shapes. Remove one.`);
  }

  const headlines = zones.texts.filter((text) => text.role === 'headline').length;
  if (headlines > 1) {
    problems.push('Only one text box can be the headline. Give the others another role.');
  }

  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const id of [...zones.texts.map((t) => t.id), ...zones.shapes.map((s) => s.id)]) {
    if (seen.has(id)) duplicates.add(id);
    seen.add(id);
  }
  for (const id of duplicates) {
    problems.push(`Two layers share the id “${id}”. Every layer needs its own.`);
  }

  if (zones.canvas.kind === 'color') {
    push(colorProblem(zones.canvas.color, 'The backdrop', false));
  } else if (zones.canvas.kind === 'gradient') {
    push(colorProblem(zones.canvas.from, 'The gradient start', false));
    push(colorProblem(zones.canvas.to, 'The gradient end', false));
    const { angle } = zones.canvas;
    if (!Number.isInteger(angle) || angle % ANGLE_STEP !== 0 || angle < 0 || angle > 315) {
      problems.push('The gradient angle has to be a multiple of 45 degrees, 0 to 315.');
    }
  }

  for (const text of zones.texts) {
    const name = textName(text);
    if (!LAYER_ID_PATTERN.test(text.id)) {
      problems.push(
        `${name} has the id “${text.id}”. Ids start with a lowercase letter and use only a-z, 0-9 and _, up to 24 characters.`,
      );
    }
    const label = text.label.trim();
    if (label.length === 0) problems.push(`A text box (${text.id}) needs a field label.`);
    if (label.length > LABEL_MAX_LENGTH) {
      problems.push(`${name} has a label over ${LABEL_MAX_LENGTH} characters.`);
    }
    if (text.max_chars < MAX_CHARS_MIN || text.max_chars > MAX_CHARS_MAX) {
      problems.push(`${name} allows ${text.max_chars} characters; keep it 1 to ${MAX_CHARS_MAX}.`);
    }
    if (text.default.length > text.max_chars) {
      problems.push(
        `${name} has default text of ${text.default.length} characters, over its limit of ${text.max_chars}.`,
      );
    }
    if (isAiBox(text) && text.max_chars < AI_MIN_CHARS) {
      problems.push(
        `${name} is filled by AI, so it needs room for at least ${AI_MIN_CHARS} characters.`,
      );
    }
    const hint = text.ai_hint ?? '';
    if (hint.length > AI_HINT_MAX) {
      problems.push(
        `${name} has AI instructions of ${hint.length} characters; keep them to ${AI_HINT_MAX}.`,
      );
    }
    push(boxProblem(text.box, name, TEXT_MINIMUM));
    if (text.size_max < SIZE_MAX_MIN || text.size_max > SIZE_MAX_MAX) {
      problems.push(
        `${name} has a largest size of ${text.size_max}px; keep it ${SIZE_MAX_MIN} to ${SIZE_MAX_MAX}.`,
      );
    }
    if (text.size_min < SIZE_MIN_MIN) {
      problems.push(`${name} has a smallest size under ${SIZE_MIN_MIN}px.`);
    }
    if (text.size_min > text.size_max) {
      problems.push(`${name} has a smallest size larger than its largest size.`);
    }
    if (text.max_lines < MAX_LINES_MIN || text.max_lines > MAX_LINES_MAX) {
      problems.push(`${name} allows ${text.max_lines} lines; keep it 1 to ${MAX_LINES_MAX}.`);
    }
    if (text.tracking < TRACKING_MIN || text.tracking > TRACKING_MAX) {
      problems.push(`${name} has letter spacing outside 0 to ${TRACKING_MAX}px.`);
    }
    if (text.line_spacing < LINE_SPACING_MIN || text.line_spacing > LINE_SPACING_MAX) {
      problems.push(`${name} has line spacing outside ${LINE_SPACING_MIN} to ${LINE_SPACING_MAX}.`);
    }
    push(colorProblem(text.color, name, true));
    const font = text.font ?? null;
    if (font !== null && !FONT_SLUG_PATTERN.test(font)) {
      problems.push(`${name} names a font that is not in our list. Pick one in the gallery.`);
    } else if (font !== null && knownFonts !== null && !knownFonts.has(font)) {
      problems.push(
        `${name} uses the font “${font}”, which is not offered any more. Pick another in the gallery, or go back to the brand font.`,
      );
    }
  }

  for (const shape of zones.shapes) {
    const name = shapeName(shape);
    if (!LAYER_ID_PATTERN.test(shape.id)) {
      problems.push(`${name} has an id that is not a lowercase letter followed by a-z, 0-9 or _.`);
    }
    push(boxProblem(shape.box, name, SHAPE_MINIMUM));
    push(colorProblem(shape.fill, name, false));
    if (shape.opacity < OPACITY_MIN || shape.opacity > OPACITY_MAX) {
      problems.push(`${name} has an opacity outside ${percent(OPACITY_MIN)}% to 100%.`);
    }
    if (shape.radius < RADIUS_MIN || shape.radius > RADIUS_MAX) {
      problems.push(`${name} has a corner radius outside 0 to ${percent(RADIUS_MAX)}%.`);
    }
    if (shape.thickness < THICKNESS_MIN || shape.thickness > THICKNESS_MAX) {
      problems.push(`${name} has a line thickness outside the allowed range.`);
    }
  }

  if (zones.logo.enabled) push(boxProblem(zones.logo.box, 'The logo box', LOGO_MINIMUM));
  if (zones.value) push(boxProblem(zones.value, 'The value box', VALUE_MINIMUM));

  if (zones.portrait) {
    const { cx, cy, d } = zones.portrait;
    if (d < PORTRAIT_MIN_D || d > PORTRAIT_MAX_D) {
      problems.push(
        `The portrait circle has to be between ${percent(PORTRAIT_MIN_D)}% and ${percent(
          PORTRAIT_MAX_D,
        )}% of the design across.`,
      );
    } else if (cx - d / 2 < 0 || cy - d / 2 < 0 || cx + d / 2 > 1 || cy + d / 2 > 1) {
      problems.push('The portrait circle hangs off the design.');
    }
  }

  return problems;
}

/* ------------------------------------------------------------------ */
/* Parsing untrusted input                                             */
/* ------------------------------------------------------------------ */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function readBox(value: unknown): ZoneBox | null {
  if (!isRecord(value)) return null;
  const x = readNumber(value.x);
  const y = readNumber(value.y);
  const w = readNumber(value.w);
  const h = readNumber(value.h);
  if (x === null || y === null || w === null || h === null) return null;
  return { x, y, w, h };
}

function readPortrait(value: unknown): PortraitZone | null {
  if (!isRecord(value)) return null;
  const cx = readNumber(value.cx);
  const cy = readNumber(value.cy);
  const d = readNumber(value.d);
  if (cx === null || cy === null || d === null) return null;
  return { cx, cy, d };
}

function readOneOf<T extends string>(value: unknown, options: readonly T[], fallback: T): T {
  return typeof value === 'string' && (options as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function readColor(value: unknown, fallback: string): string {
  return typeof value === 'string' && isColorRef(value) ? value : fallback;
}

function readCanvas(value: unknown): CommunityCanvas {
  if (!isRecord(value)) return { kind: 'image' };
  switch (value.kind) {
    case 'color':
      return { kind: 'color', color: readColor(value.color, 'brand:primary') };
    case 'gradient':
      return {
        kind: 'gradient',
        from: readColor(value.from, 'brand:primary'),
        to: readColor(value.to, 'brand:secondary'),
        angle: readNumber(value.angle) ?? 135,
      };
    default:
      return { kind: 'image' };
  }
}

const TEXT_ROLE_VALUES: readonly CommunityTextRole[] = TEXT_ROLE_ORDER;
const SHAPE_KINDS: readonly CommunityShapeKind[] = ['rect', 'ellipse', 'line'];
const LOGO_STYLES: readonly CommunityLogoStyle[] = ['auto', 'bare', 'chip'];

function readText(value: unknown, index: number): CommunityTextBox | null {
  if (!isRecord(value)) return null;
  const box = readBox(value.box);
  if (!box) return null;
  const role = readOneOf(value.role, TEXT_ROLE_VALUES, 'body');
  const preset = TEXT_ROLES[role];
  return {
    id: readString(value.id, `text_${index + 1}`),
    role,
    label: readString(value.label, preset.label),
    default: readString(value.default, ''),
    max_chars: readNumber(value.max_chars) ?? preset.maxChars,
    box,
    align: readOneOf(value.align, ['left', 'center', 'right'] as const, 'left'),
    valign: readOneOf(value.valign, ['top', 'middle', 'bottom'] as const, 'middle'),
    weight: readOneOf(value.weight, ['bold', 'regular'] as const, preset.weight),
    size_max: readNumber(value.size_max) ?? preset.sizeMax,
    size_min: readNumber(value.size_min) ?? preset.sizeMin,
    max_lines: readNumber(value.max_lines) ?? preset.maxLines,
    uppercase: typeof value.uppercase === 'boolean' ? value.uppercase : preset.uppercase,
    tracking: readNumber(value.tracking) ?? preset.tracking,
    line_spacing: readNumber(value.line_spacing) ?? preset.lineSpacing,
    color: typeof value.color === 'string' && isTextColor(value.color) ? value.color : 'auto',
    font: readFontSlug(value.font),
    fill: readOneOf(value.fill, TEXT_FILLS, 'user'),
    ai_hint: readString(value.ai_hint, ''),
  };
}

function readShape(value: unknown, index: number): CommunityShape | null {
  if (!isRecord(value)) return null;
  const box = readBox(value.box);
  if (!box) return null;
  return {
    id: readString(value.id, `s${index + 1}`),
    kind: readOneOf(value.kind, SHAPE_KINDS, 'rect'),
    box,
    fill: readColor(value.fill, 'brand:accent'),
    opacity: readNumber(value.opacity) ?? 1,
    radius: readNumber(value.radius) ?? 0,
    thickness: readNumber(value.thickness) ?? 0.004,
  };
}

function parseV1(record: Record<string, unknown>): CommunityZonesV1 | null {
  const text = readBox(record.text);
  const logo = readBox(record.logo);
  if (!text || !logo) return null;
  return {
    v: 1,
    text: clampBox(text, { w: V1_TEXT_MIN_W, h: V1_TEXT_MIN_H }),
    align: record.align === 'center' ? 'center' : 'left',
    logo: clampBox(logo, LOGO_MINIMUM),
    portrait: (() => {
      const portrait = readPortrait(record.portrait);
      return portrait ? clampPortrait(portrait) : null;
    })(),
    value: (() => {
      const value = readBox(record.value);
      return value ? clampBox(value, VALUE_MINIMUM) : null;
    })(),
    ink: record.ink === 'light' || record.ink === 'dark' ? record.ink : 'auto',
  };
}

function parseV2(record: Record<string, unknown>): CommunityZonesV2 | null {
  if (!Array.isArray(record.texts)) return null;
  const texts = record.texts
    .map(readText)
    .filter((text): text is CommunityTextBox => text !== null);
  const shapes = Array.isArray(record.shapes)
    ? record.shapes.map(readShape).filter((shape): shape is CommunityShape => shape !== null)
    : [];
  const logoRecord = isRecord(record.logo) ? record.logo : {};
  const logo: CommunityLogo = {
    enabled: typeof logoRecord.enabled === 'boolean' ? logoRecord.enabled : true,
    box: readBox(logoRecord.box) ?? DEFAULT_LOGO_BOX,
    style: readOneOf(logoRecord.style, LOGO_STYLES, 'auto'),
  };
  return clampZones({
    v: 2,
    canvas: readCanvas(record.canvas),
    shapes,
    texts,
    logo,
    portrait: readPortrait(record.portrait),
    value: readBox(record.value),
  });
}

/**
 * A zone map out of untrusted input — router state, or a suggestion the import
 * read off a document — clamped into legality, or null when the shape is not a
 * map at all. A v1 map keeps its version (the gallery and the brief read both);
 * a map with no `v` is read as v1, which is what every map was before v2.
 *
 * A half-map is rejected rather than repaired: inventing a caption box would
 * put text somewhere the author never looked.
 */
export function parseCommunityZones(value: unknown): CommunityZones | null {
  if (!isRecord(value)) return null;
  if (value.v === 2) return parseV2(value);
  return parseV1(value);
}

/** Box geometry for a text box's field in the brief: label, sample, limit. */
export function textFieldsOf(zones: CommunityZones): CommunityTextBox[] {
  return zones.v === 2 ? zones.texts : [];
}
